import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "../../lib/utils"

/**
 * Heading component with semantic HTML and theme-based variants
 */
const headingVariants = cva(
    "font-bold text-foreground tracking-tight",
    {
        variants: {
            level: {
                h1: "text-5xl leading-tight",
                h2: "text-4xl leading-tight",
                h3: "text-3xl leading-tight",
                h4: "text-2xl leading-normal",
                h5: "text-xl leading-normal",
                h6: "text-lg leading-normal",
            },
        },
        defaultVariants: {
            level: "h2",
        },
    }
)

export const Heading = React.forwardRef(
    ({ className, level = "h2", as, ...props }, ref) => {
        const Comp = as || level
        return (
            <Comp
                ref={ref}
                className={cn(headingVariants({ level, className }))}
                {...props}
            />
        )
    }
)
Heading.displayName = "Heading"

/**
 * Text component for body content
 */
const textVariants = cva(
    "text-foreground",
    {
        variants: {
            size: {
                xs: "text-xs",
                sm: "text-sm",
                base: "text-base",
                lg: "text-lg",
                xl: "text-xl",
            },
            variant: {
                default: "text-foreground",
                muted: "text-muted-foreground",
                accent: "text-accent-foreground",
                destructive: "text-destructive",
            },
            weight: {
                normal: "font-normal",
                medium: "font-medium",
                semibold: "font-semibold",
                bold: "font-bold",
            },
        },
        defaultVariants: {
            size: "base",
            variant: "default",
            weight: "normal",
        },
    }
)

export const Text = React.forwardRef(
    ({ className, size, variant, weight, as: Comp = "p", ...props }, ref) => {
        return (
            <Comp
                ref={ref}
                className={cn(textVariants({ size, variant, weight, className }))}
                {...props}
            />
        )
    }
)
Text.displayName = "Text"

/**
 * Label component for form labels and small text
 */
const labelVariants = cva(
    "text-sm font-medium leading-normal",
    {
        variants: {
            variant: {
                default: "text-foreground",
                muted: "text-muted-foreground",
                destructive: "text-destructive",
            },
            required: {
                true: "after:content-['*'] after:ml-0.5 after:text-destructive",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
)

export const Label = React.forwardRef(
    ({ className, variant, required, ...props }, ref) => {
        return (
            <label
                ref={ref}
                className={cn(labelVariants({ variant, required, className }))}
                {...props}
            />
        )
    }
)
Label.displayName = "Label"

/**
 * Code component for inline code snippets
 */
export const Code = React.forwardRef(
    ({ className, ...props }, ref) => {
        return (
            <code
                ref={ref}
                className={cn(
                    "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold text-foreground",
                    className
                )}
                {...props}
            />
        )
    }
)
Code.displayName = "Code"

/**
 * Lead text for introductory paragraphs
 */
export const Lead = React.forwardRef(
    ({ className, ...props }, ref) => {
        return (
            <p
                ref={ref}
                className={cn("text-xl text-muted-foreground", className)}
                {...props}
            />
        )
    }
)
Lead.displayName = "Lead"

/**
 * Small text component
 */
export const Small = React.forwardRef(
    ({ className, ...props }, ref) => {
        return (
            <small
                ref={ref}
                className={cn("text-sm font-medium leading-normal", className)}
                {...props}
            />
        )
    }
)
Small.displayName = "Small"

/**
 * Muted text component
 */
export const Muted = React.forwardRef(
    ({ className, ...props }, ref) => {
        return (
            <p
                ref={ref}
                className={cn("text-sm text-muted-foreground", className)}
                {...props}
            />
        )
    }
)
Muted.displayName = "Muted"


